import type {DiffLine} from '@github-ui/diff-lines/types'
import type {Group, GroupHunkLine} from '../types/group-types'

/**
 * @param diffLines - diff lines to be filtered
 * @param group - A Copilot generated group that contains hunk positions for specific paths.
 * @param path - The path of a diff entry that will be used to key on a group's hunk positions record.
 * @returns a filtered or equal list of diff lines (e.g. DiffLine[]) that are part of the hunks in a group
 */
export function scopeToDiffLinesByGroup(diffLines: DiffLine[], group: Group, path: string): DiffLine[] {
  const scopedDiffLines: DiffLine[] = []
  const pathHunks = group.hunkPositions[path]

  if (!pathHunks) return scopedDiffLines

  let currentHunkIndex = 0
  let currentHunk = pathHunks[currentHunkIndex]

  if (!currentHunk) return scopedDiffLines

  let inMatchingHunk = false

  for (const line of diffLines) {
    if (!inMatchingHunk) {
      if (isMatchingLine(line, currentHunk.startLine)) {
        inMatchingHunk = true
        // Add Hunk header line stored in the current hunk
        scopedDiffLines.push(currentHunk.headerLine)
        // Add first non "HUNK" type line
        scopedDiffLines.push(line)
      }
    } else {
      // Always push line into scopedDiffLines if actively adding lines
      scopedDiffLines.push(line)

      // If line is a matching line, stop actively adding lines and move to the next hunk
      if (isMatchingLine(line, currentHunk.endLine)) {
        inMatchingHunk = false
        currentHunkIndex++
        currentHunk = pathHunks[currentHunkIndex]
        // Break diff line iteration if there are no more hunks to evaluate
        if (!currentHunk) break
      }
    }
  }

  return scopedDiffLines
}

function isMatchingLine(line: DiffLine, hunkLine: GroupHunkLine) {
  // If types don't align we don't need to evaluate further
  if (line.type !== hunkLine.type) return false

  const lineIsContextOrAddition = line.type === 'CONTEXT' || line.type === 'ADDITION'
  const lineIsDeletion = line.type === 'DELETION'
  const hasMatchingLine =
    (lineIsContextOrAddition && line.right === hunkLine.line) || (lineIsDeletion && line.left === hunkLine.line)

  return hasMatchingLine
}
